import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PostDto, PostRequestDto } from 'src/api/FiretrolApiClient';
import { postApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { queryKeys } from 'src/api/queries/queryKeys';

export function useCreatePost(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: PostRequestDto) =>
      postApiClient.createPost(jobId, request),
    onSettled: () => invalidateFor('post.write', qc, { jobId }),
  });
}

export function useUpdatePost(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      postId,
      request,
    }: {
      postId: string;
      request: PostRequestDto;
    }) => postApiClient.updatePost(jobId, postId, request),
    onSettled: () => invalidateFor('post.write', qc, { jobId }),
  });
}

export function useDeletePost(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => postApiClient.deletePost(jobId, postId),
    meta: { successMessage: 'Post Successfully Deleted' },
    onSettled: () => invalidateFor('post.write', qc, { jobId }),
  });
}

interface IResolveContext {
  previous: PostDto | undefined;
}

export function useResolvePost(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, resolved }: { postId: string; resolved: boolean }) =>
      postApiClient.resolvePost(jobId, postId, { resolved }),
    meta: {
      successMessageFn: (data: unknown) =>
        (data as PostDto).resolved ? 'Post Resolved!' : 'Post Unresolved!',
    },
    onMutate: async ({
      postId,
      resolved,
    }): Promise<IResolveContext | undefined> => {
      const key = queryKeys.posts.detail(jobId, postId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PostDto>(key);
      if (previous) {
        qc.setQueryData<PostDto>(key, { ...previous, resolved } as PostDto);
      }
      return { previous };
    },
    onError: (_err, { postId }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(queryKeys.posts.detail(jobId, postId), ctx.previous);
      }
    },
    onSettled: () => invalidateFor('post.write', qc, { jobId }),
  });
}
